import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';

const nav = [
  { to: '/super-admin', icon: '📊', label: 'Dashboard' },
  { to: '/super-admin/businesses', icon: '🏢', label: 'Businesses' },
  { to: '/super-admin/payments', icon: '💰', label: 'Payments' },
  { to: '/super-admin/invoice-report', icon: '🧾', label: 'Invoice Report' },
  { to: '/super-admin/tickets', icon: '🎫', label: 'Support Tickets' },

  { to: '/super-admin/plans', icon: '📦', label: 'Plans' },
  { to: '/super-admin/agents', icon: '👥', label: 'Agent Requests' }
];

const STATUS_COLORS = { open: '#f59e0b', in_progress: '#3b82f6', resolved: '#00d55b', closed: '#555' };

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [status, setStatus] = useState('open');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTickets(); }, [status]);

  const loadTickets = () => {
    setLoading(true);
    axios.get(`/api/tickets/admin/all?status=${status}`).then(r => {
      setTickets(r.data.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const loadTicket = async (ticket) => {
    const r = await axios.get(`/api/tickets/admin/${ticket.id}`);
    setSelected(r.data.data);
  };

  const sendReply = async (newStatus) => {
    if (!reply.trim()) return toast.error('Type a reply first');
    try {
      await axios.post(`/api/tickets/admin/${selected.id}/reply`, { message: reply, status: newStatus });
      toast.success('Reply sent!');
      setReply('');
      loadTicket(selected);
      loadTickets();
    } catch { toast.error('Failed'); }
  };

  return (
    <DashLayout navItems={nav} role="Super Admin">
      <div style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>Support Tickets</h1>
            <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Client support requests</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['open', 'in_progress', 'resolved', 'closed', 'all'].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${status === s ? '#00d55b' : '#1e1e1e'}`, background: status === s ? 'rgba(0,213,91,0.1)' : '#111', color: status === s ? '#00d55b' : '#555', fontSize: 12, cursor: 'pointer', textTransform: 'capitalize' }}>
                {s.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 420px' : '1fr', gap: 20 }}>
          {/* List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#333' }}>Loading...</div>
            ) : tickets.length === 0 ? (
              <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 40, textAlign: 'center', color: '#333' }}>No tickets</div>
            ) : tickets.map(ticket => (
              <div key={ticket.id} onClick={() => loadTicket(ticket)}
                style={{ background: selected?.id === ticket.id ? 'rgba(0,213,91,0.05)' : '#111', border: `1px solid ${selected?.id === ticket.id ? '#00d55b' : '#1e1e1e'}`, borderRadius: 12, padding: '16px 20px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', background: '#1a1a1a', borderRadius: 20, color: '#888' }}>{ticket.category}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: `${STATUS_COLORS[ticket.status]}22`, color: STATUS_COLORS[ticket.status], fontWeight: 600 }}>
                      {ticket.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: '#555' }}>{new Date(ticket.created_at).toLocaleDateString('en-IN')}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>#{ticket.id} — {ticket.subject}</div>
                <div style={{ fontSize: 12, color: '#555' }}>
                  <span style={{ color: '#888' }}>{ticket.company_name}</span> · {ticket.reply_count || 0} replies
                </div>
              </div>
            ))}
          </div>

          {/* Detail */}
          {selected && (
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 20, position: 'sticky', top: 20, height: 'fit-content' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>#{selected.id} — {selected.subject}</div>
                  <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
                    {selected.company_name} · <span style={{ color: STATUS_COLORS[selected.status] }}>{selected.status?.replace('_', ' ')}</span>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>

              {/* Messages */}
              <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {(selected.replies || []).map((r, i) => (
                  <div key={i} style={{ padding: '10px 14px', borderRadius: 10, background: r.sender_type === 'admin' ? 'rgba(0,213,91,0.08)' : '#1a1a1a', border: `1px solid ${r.sender_type === 'admin' ? 'rgba(0,213,91,0.2)' : '#1e1e1e'}`, alignSelf: r.sender_type === 'admin' ? 'flex-end' : 'flex-start', maxWidth: '92%' }}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>
                      {r.sender_type === 'admin' ? '🛡 You (Admin)' : '🏢 ' + selected.company_name} · {new Date(r.created_at).toLocaleString('en-IN')}
                    </div>
                    <div style={{ fontSize: 13, color: '#ddd', lineHeight: 1.5 }}>{r.message}</div>
                  </div>
                ))}
              </div>

              {/* Reply */}
              {selected.status !== 'closed' && (
                <div>
                  <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." rows={3}
                    style={{ width: '100%', background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13, padding: '8px 12px', resize: 'none', outline: 'none', fontFamily: 'inherit', marginBottom: 8 }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => sendReply('in_progress')}
                      style={{ flex: 1, padding: '9px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}>
                      Send Reply
                    </button>
                    <button onClick={() => sendReply('resolved')}
                      style={{ flex: 1, padding: '9px', background: 'rgba(0,213,91,0.1)', color: '#00d55b', border: '1px solid #00d55b', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                      Resolve
                    </button>
                    <button onClick={() => sendReply('closed')}
                      style={{ padding: '9px 14px', background: '#1a1a1a', color: '#555', border: '1px solid #1e1e1e', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                      Close
                    </button>
                  </div>
                </div>
              )}
              {selected.status === 'closed' && (
                <div style={{ textAlign: 'center', color: '#555', fontSize: 13, padding: 16 }}>This ticket is closed.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashLayout>
  );
}